import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import BrandIcon from "../components/app/BrandIcon";
import { useTranslation } from "react-i18next";
import api, { extractAxiosError } from "../lib/api";

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [devUrl, setDevUrl] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setDevUrl("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setDone(true);
      if (data?.dev_reset_url) setDevUrl(data.dev_reset_url);
    } catch (err) {
      setError(extractAxiosError(err, t("auth.forgotErrorGeneric")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="theme-swiss min-h-screen flex items-center justify-center p-6 bg-slate-900">
      <form
        onSubmit={submit}
        className="w-full max-w-md fade-in-up"
        data-testid="forgot-password-form"
      >
        <Link to="/" className="flex items-center gap-3 mb-8 group" data-testid="logo-home">
          <BrandIcon size={40} />
          <div className="font-display font-bold text-xl text-slate-100 group-hover:text-orange-300 transition-colors">MachinePilot</div>
        </Link>

        <div className="label-tech mb-2">{t("auth.accessTitle")}</div>
        <h2 className="font-display font-bold text-3xl text-slate-100">
          {t("auth.forgotTitle")}
        </h2>
        <p className="text-slate-400 mt-2 text-sm">{t("auth.forgotSubtitle")}</p>

        {!done && (
          <>
            <div className="mt-8">
              <label className="label-tech block mb-2">{t("auth.email")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-md text-sm"
                required
                data-testid="forgot-email"
              />
            </div>

            {error && (
              <div
                className="mt-4 px-4 py-3 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 text-sm"
                data-testid="forgot-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full mt-8 px-5 py-3 rounded-md flex items-center justify-center gap-2"
              data-testid="forgot-submit"
            >
              {busy ? t("auth.sending") : (<>{t("auth.sendResetLink")} <ArrowRight size={16} /></>)}
            </button>
          </>
        )}

        {done && (
          <div
            className="mt-8 px-4 py-4 rounded-md bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 text-sm"
            data-testid="forgot-success"
          >
            <p className="font-medium">{t("auth.forgotSuccessTitle")}</p>
            <p className="mt-1 text-emerald-100/80">{t("auth.forgotSuccessBody")}</p>
            {devUrl && (
              <div className="mt-3 text-xs text-emerald-300 break-all" data-testid="forgot-dev-url">
                {t("auth.resendPreviewPrefix")}: {devUrl}
              </div>
            )}
          </div>
        )}

        <div className="mt-6 text-sm text-slate-400 text-center">
          <Link
            to="/login"
            className="text-blue-400 hover:text-blue-300 font-medium inline-flex items-center gap-1"
            data-testid="back-to-login"
          >
            <ArrowLeft size={14} /> {t("auth.backToLogin")}
          </Link>
        </div>
      </form>
    </div>
  );
}
